import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { sendPartnerWhatsappAlert } from '../../api/endpoints/admin';
import type { WhatsappAlert } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { StatusPill } from '../../components/ui/StatusPill';
import { PARTNER_ALERT_COPY, alertPill, alertSentAtLabel, isAlreadyNotified, isUnconfirmed, sendFailureCopy } from './partnerAlert';
import styles from './FleetManagerSuggestions.module.css';

interface Props {
  shipmentId: number;
  vendorId: number;
  /** Server state for this partner this cycle; null = no partner id, so nothing to show. */
  alert: WhatsappAlert | null;
}

type Notice = { kind: 'already' } | { kind: 'error'; copy: string; retryId: string | null };

/**
 * One suggestion row's load alert (#289, R44): the server's state as a pill, and "Send WhatsApp"
 * only when the server says `canSend` (rhino-api R34/R35). The send is recorded by the API.
 */
export function PartnerAlertCell({ shipmentId, vendorId, alert }: Props) {
  const queryClient = useQueryClient();
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  // The send's answer until the refetched row arrives, so the button never flashes back.
  const [answer, setAnswer] = useState<WhatsappAlert | null>(null);
  useEffect(() => setAnswer(null), [alert]);

  if (!alert) return null;
  const shown = answer ?? alert;
  const pill = alertPill(shown);
  const sentAt = alertSentAtLabel(shown);

  const refreshRow = () =>
    void queryClient.invalidateQueries({ queryKey: ['admin', 'service-lanes', 'suggestions', shipmentId] });

  const send = async (clientRequestId: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      setAnswer(await sendPartnerWhatsappAlert(shipmentId, vendorId, clientRequestId));
      refreshRow();
    } catch (error) {
      if (isAlreadyNotified(error)) {
        setNotice({ kind: 'already' });
        refreshRow();
      } else {
        setNotice({ kind: 'error', copy: sendFailureCopy(error), retryId: isUnconfirmed(error) ? clientRequestId : null });
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className={styles.alertCell}>
      {pill && <StatusPill bg={pill.bg} text={pill.text} label={pill.label} icon={<StatusIcon name={pill.icon} />} />}
      {sentAt && <span className={styles.alertTime}>{sentAt}</span>}
      {shown.canSend && notice?.kind !== 'error' && (
        <Button variant="secondary" size="sm" className={styles.sendBtn} busy={busy} onClick={() => void send(crypto.randomUUID())}>
          <StatusIcon name="chat" />
          {busy ? 'Sending…' : 'Send WhatsApp'}
        </Button>
      )}
      {notice?.kind === 'already' && <span className={styles.alertNote}>{PARTNER_ALERT_COPY.ALREADY_NOTIFIED}</span>}
      {notice?.kind === 'error' && (
        <>
          <span className={styles.alertError} role="alert">{notice.copy}</span>
          <Button variant="secondary" size="sm" busy={busy} onClick={() => void send(notice.retryId ?? crypto.randomUUID())}>
            {busy ? 'Sending…' : 'Retry'}
          </Button>
        </>
      )}
    </div>
  );
}
